// ---------------------------------------------------------------------------
// LanguageSwitcher — admin-scoped locale picker (ADMIN_LOCALES only).
// Persists selection via the languageChanged listener wired in main.tsx.
// ---------------------------------------------------------------------------

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADMIN_LOCALES,
  LOCALE_METADATA,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@noxverify/i18n';

export interface LanguageSwitcherProps {
  /** Optional className passthrough so the host can position the control. */
  className?: string;
}

export function LanguageSwitcher({ className = '' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();

  const current: SupportedLocale = isSupportedLocale(i18n.language)
    ? (i18n.language as SupportedLocale)
    : DEFAULT_LOCALE;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      if (isSupportedLocale(next) && ADMIN_LOCALES.includes(next)) {
        void i18n.changeLanguage(next);
      }
    },
    [i18n],
  );

  return (
    <label className={`inline-flex items-center gap-2 text-sm ${className}`}>
      <span className="sr-only">{t('language.switcher', 'Choose language')}</span>
      <span aria-hidden="true" className="text-ink-muted">
        {/* Globe glyph (decorative) */}
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21a9 9 0 100-18 9 9 0 000 18zm0 0a9 9 0 01-9-9m9 9a9 9 0 009-9m-18 0h18M12 3a14 14 0 010 18m0-18a14 14 0 000 18"
          />
        </svg>
      </span>
      <select
        value={current}
        onChange={handleChange}
        className="rounded-sm border border-hairline-strong bg-white px-2 py-1 text-sm text-ink hover:border-ink focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-colors"
        aria-label={t('language.switcher', 'Choose language')}
      >
        {ADMIN_LOCALES.map((locale) => {
          const meta = LOCALE_METADATA[locale];
          return (
            <option key={locale} value={locale}>
              {meta.nativeName}
            </option>
          );
        })}
      </select>
    </label>
  );
}
